import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FileCheck2, Info } from 'lucide-react';
interface SlideGovernanceProps {
  onContinue: () => void;
}
const PURPLE = '#6C2BD9';
type Row = {
  step?: string;
  activity: string;
  decides: string;
  executes: string;
  approves: string;
  tier?: 'key' | 'secondary';
};
const ROWS: Row[] = [{
  step: '01',
  activity: 'Existing approved content, claims & narrative',
  decides: 'Global brand team',
  executes: 'LEO production',
  approves: 'Global MLR'
}, {
  step: '02',
  activity: 'Snakkerfy — Master Snak for short engagement',
  decides: 'Global brand team',
  executes: 'Snakker',
  approves: 'Global MLR'
}, {
  activity: 'Market tiering & campaign scope',
  decides: 'Global brand team',
  executes: 'Global + Snakker',
  approves: 'Global brand team'
}, {
  step: '03',
  activity: 'Localization & adaptation',
  decides: 'Local affiliate',
  executes: 'Snakker',
  approves: 'Local affiliate + MLR',
  tier: 'key'
}, {
  step: '03',
  activity: 'Localization & adaptation',
  decides: 'Global brand team',
  executes: 'Snakker',
  approves: 'Local sign-off only',
  tier: 'secondary'
}, {
  step: '04',
  activity: 'Deployment to LEO channels (web, email, IVA)',
  decides: 'Local affiliate',
  executes: 'LEO production team',
  approves: 'Local affiliate'
}, {
  activity: 'Optimization loop & performance reporting',
  decides: 'Snakker recommends',
  executes: 'Snakker',
  approves: 'Global brand team'
}, {
  activity: 'Portfolio visibility (Control Tower)',
  decides: 'Global brand team',
  executes: 'Snakker',
  approves: '—'
}];
const NOTES = [{
  title: 'Key markets keep local ownership',
  body: 'Priority markets brief their own campaigns and sign them off — the model they already run today. Snakker executes.'
}, {
  title: 'Secondary markets need no local effort',
  body: 'Global decides the scope, Snakker executes end to end, and the affiliate only signs off. No local project, no local resourcing.'
}, {
  title: 'Approvals never move',
  body: 'MLR and local sign-off stay exactly where they sit today. Snakker executes inside those gates, never around them.'
}];
export function SlideGovernance({
  onContinue
}: SlideGovernanceProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const timers = [200, 800, 1600].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full bg-white overflow-y-auto" data-id="element-677" data-mp="10rf28tsc57r8">
      <div className="min-h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-678" data-mp="15rjhyhlf5yge">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-3 shrink-0" data-id="element-679" data-mp="1thdvl1dkbr1p">
          <p className="font-semibold tracking-widest uppercase text-sm" style={{
          color: PURPLE
        }} data-id="element-680" data-mp="3ixhkxn0d2b3o">
            The Operating Model
          </p>
          <h2 className="text-3xl md:text-5xl font-serif text-slate-900" data-id="element-681" data-mp="3sn11un4ijuvw">
            Who decides, who executes, who approves
          </h2>
          <p className="text-lg text-slate-500" data-id="element-682" data-mp="3nr2gl8eheszp">
            The same four steps — approved content, Snakkerfy, localize, publish
            — with the ownership made explicit.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: stage >= 1 ? 1 : 0,
        y: stage >= 1 ? 0 : 16
      }} className="mt-8 rounded-2xl border border-slate-200 overflow-hidden" data-id="element-683" data-mp="3903coaolcsmg">
          <table className="w-full text-left border-collapse" data-id="element-684" data-mp="k899neg7l2ei">
            <caption className="sr-only" data-id="element-685" data-mp="k7pntmnfttv7">
              Operating model: decision rights, execution and approvals per
              activity
            </caption>
            <thead data-id="element-686" data-mp="1jj95jk1noww3">
              <tr className="bg-slate-50" data-id="element-687" data-mp="1jksltanbb7xv">
                {['Activity', 'Decides', 'Executes', 'Approves'].map(head => <th key={head} scope="col" className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-688" data-mp="1ti71v95bpupn">
                      {head}
                    </th>)}
              </tr>
            </thead>
            <tbody data-id="element-689" data-mp="27awnvdkuqzmr">
              {ROWS.map((row, idx) => <motion.tr key={`${row.activity}-${row.tier ?? 'all'}`} initial={{
              opacity: 0
            }} animate={{
              opacity: stage >= 2 ? 1 : 0
            }} transition={{
              delay: stage >= 2 ? idx * 0.07 : 0
            }} className="border-t border-slate-100" data-id="element-690" data-mp="2c8exz7alvkwc" data-mpi={idx}>
                  <th scope="row" className="px-4 py-3.5 text-sm font-semibold text-slate-900 align-top" data-id="element-691" data-mp="5j4omxequ091">
                    <span className="flex flex-col gap-1.5" data-id="element-692" data-mp="3rspfgmumqzmv">
                      <span className="flex items-baseline gap-2" data-id="element-693" data-mp="ta0li8y23ln5">
                        <span className="text-[11px] font-bold tabular-nums w-5 shrink-0" style={{
                      color: row.step ? PURPLE : 'transparent'
                    }} data-id="element-694" data-mp="t98pwy4glnwv">
                          {row.step ?? '—'}
                        </span>
                        {row.activity}
                      </span>
                      {row.tier && <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full self-start ml-7" style={row.tier === 'key' ? {
                    color: PURPLE,
                    backgroundColor: 'rgba(108,43,217,0.10)'
                  } : {
                    color: '#0f766e',
                    backgroundColor: 'rgba(13,148,136,0.10)'
                  }} data-id="element-695" data-mp="179jhy4hi98jm">
                          {row.tier === 'key' ? 'Key markets' : 'Secondary / legacy markets'}
                        </span>}
                    </span>
                  </th>

                  <td className="px-4 py-3.5 text-sm text-slate-600 align-top" data-id="element-696" data-mp="72eeleatdw1h">
                    {row.decides}
                  </td>
                  <td className="px-4 py-3.5 text-sm align-top" data-id="element-697" data-mp="1wiqidw9dvtgr">
                    <span className={row.executes.includes('Snakker') ? 'font-semibold' : 'text-slate-600'} style={row.executes.includes('Snakker') ? {
                  color: PURPLE
                } : undefined} data-id="element-698" data-mp="1wj05nc31nmir">
                      {row.executes}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-slate-600 align-top" data-id="element-699" data-mp="2p6w3wl89hmhn">
                    {row.approves}
                  </td>
                </motion.tr>)}
            </tbody>
          </table>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        y: stage >= 3 ? 0 : 12
      }} className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" data-id="element-700" data-mp="pua4fztjsdsl">
          {NOTES.map((note, idx) => <div key={note.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5" data-id="element-701" data-mp="dqnx2ec15lg3" data-mpi={idx}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{
            backgroundColor: 'rgba(108,43,217,0.10)',
            color: PURPLE
          }} data-id="element-702" data-mp="ip7jsgyg719i">
                {idx === 0 ? <FileCheck2 className="w-4 h-4" data-id="element-703" data-mp="3r497ttuhav4i" /> : <Info className="w-4 h-4" data-id="element-704" data-mp="3v8gwjr0xsb2q" />}
              </div>
              <p className="text-slate-900 font-semibold text-sm mb-1.5" data-id="element-705" data-mp="16g4ob8nwywaq">
                {note.title}
              </p>
              <p className="text-slate-500 text-sm leading-relaxed" data-id="element-706" data-mp="1ag5etroaexy5">
                {note.body}
              </p>
            </div>)}
        </motion.div>
      </div>
    </div>;
}